import { useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { customerDocumentsApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import {
  Upload,
  File,
  FileText,
  Image,
  Film,
  Music,
  Archive,
  Download,
  Trash2,
  Loader2,
  X
} from "lucide-react";

const getFileIcon = (contentType) => {
  if (contentType?.startsWith('image/')) return Image;
  if (contentType?.startsWith('video/')) return Film;
  if (contentType?.startsWith('audio/')) return Music;
  if (contentType?.includes('pdf')) return FileText;
  if (contentType?.includes('zip') || contentType?.includes('rar') || contentType?.includes('tar')) return Archive;
  return File;
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default function DocumentUpload({ customerId, documents, onDocumentsChange }) {
  const toast = useToast();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const uploadFiles = useCallback(async (files) => {
    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of files) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: Datei zu groß (max. 10MB)`);
        errorCount++;
        continue;
      }

      try {
        await customerDocumentsApi.upload(customerId, file);
        successCount++;
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`${file.name}: Upload fehlgeschlagen`);
        errorCount++;
      }
    }

    setUploading(false);

    if (successCount > 0) {
      toast.success(`${successCount} Dokument(e) hochgeladen`);
      onDocumentsChange();
    }
  }, [customerId, onDocumentsChange, toast]);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    await uploadFiles(files);
  }, [uploadFiles]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    await uploadFiles(files);
    e.target.value = ''; // Reset input
  };

  const handleDownload = async (doc) => {
    try {
      const response = await customerDocumentsApi.download(customerId, doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Download fehlgeschlagen");
    }
  };

  const handleDelete = async (doc) => {
    setDeletingId(doc.id);
    try {
      await customerDocumentsApi.delete(customerId, doc.id);
      toast.success("Dokument gelöscht");
      onDocumentsChange();
    } catch (error) {
      toast.error("Löschen fehlgeschlagen");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer
          ${dragOver 
            ? 'border-primary bg-primary/5 scale-[1.02]' 
            : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50'
          }
        `}
        data-testid="document-dropzone"
      >
        <input
          type="file"
          multiple
          onChange={handleFileSelect}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          data-testid="document-file-input"
        />
        
        <div className="flex flex-col items-center gap-3">
          {uploading ? (
            <>
              <Loader2 className="w-10 h-10 text-primary animate-spin" />
              <p className="text-sm font-medium">Dokumente werden hochgeladen...</p>
            </>
          ) : (
            <>
              <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                dragOver ? 'bg-primary/20' : 'bg-muted'
              }`}>
                <Upload className={`w-7 h-7 ${dragOver ? 'text-primary' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <p className="text-sm font-medium">
                  Dateien hier ablegen oder <span className="text-primary">durchsuchen</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Maximale Dateigröße: 10MB
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Document List */}
      {documents && documents.length > 0 ? (
        <div className="space-y-2">
          {documents.map((doc) => {
            const FileIcon = getFileIcon(doc.content_type);
            return (
              <Card key={doc.id} className="overflow-hidden">
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                      <FileIcon className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate" title={doc.original_filename}>
                        {doc.original_filename}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{formatFileSize(doc.size)}</span>
                        <span>•</span>
                        <span>{new Date(doc.uploaded_at).toLocaleDateString('de-DE')}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDownload(doc)}
                        title="Herunterladen"
                        data-testid={`download-doc-${doc.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(doc)}
                        disabled={deletingId === doc.id}
                        title="Löschen"
                        data-testid={`delete-doc-${doc.id}`}
                      >
                        {deletingId === doc.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <File className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Keine Dokumente vorhanden</p>
        </div>
      )}
    </div>
  );
}
